import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Calendar, RotateCcw, Bell, X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { TextField } from '@mui/material';

import {
  getTraining,
  listTrainingAssignments,
  assignTraining,
  unassignTraining,
  extendTrainingAssignment,
  grantBonusAttempts,
  getTrainingEvaluationResults,
  nudgeTrainingAssignment,
} from '../../services/trainingService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import DateTimePicker from '../../components/ui/DateTimePicker';
import CheckboxInput from '../../components/ui/CheckboxInput';
import AssignmentTargetPicker, { EMPTY_TARGETS } from '../../components/ui/AssignmentTargetPicker';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import PersonCell from '../../components/ui/PersonCell';
import { parseTableParams } from '../../utils/Utils';
import { dateFilterColumn } from '../../utils/dateFilterColumn';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const Title = styled.h1`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 8px 0 12px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  margin-top: 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px;
`;

const Warning = styled.div`
  background: #FEF3C7;
  color: #92400E;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $s }) =>
    $s === 'completed' ? '#DCFCE7' :
    $s === 'in_progress' ? '#DBEAFE' :
    $s === 'failed' ? '#FEE2E2' :
    $s === 'expired' ? '#FEE2E2' : '#E0E7FF'};
  color: ${({ $s }) =>
    $s === 'completed' ? '#166534' :
    $s === 'in_progress' ? '#1E40AF' :
    $s === 'failed' ? '#991B1B' :
    $s === 'expired' ? '#991B1B' : '#3730A3'};
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const DrawerTitle = styled.h3`
  font-size: 16px; font-weight: 700; color: #004242;
  font-family: 'Nunito Sans', sans-serif;
  margin: 0;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: #F5F7FA; }
`;
const DrawerBody = styled.div`
  flex: 1; overflow-y: auto; padding: 24px;
  display: flex; flex-direction: column; gap: 16px;
`;
const DrawerFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const FooterRow = styled.div`
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;
`;

const emptyTargets = () => ({ ...EMPTY_TARGETS, excludes: { ...EMPTY_TARGETS.excludes } });

export default function TrainingAssignmentsPage() {
  const { t, i18n } = useTranslation();
  const { id: trainingId } = useParams();

  const [training, setTraining] = useState(null);
  const [trainingLoading, setTrainingLoading] = useState(true);

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState(emptyTargets());
  const [startDate, setStartDate] = useState(() => new Date());
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  });
  const [isMandatory, setIsMandatory] = useState(true);
  const [notificationEnabled, setNotificationEnabled] = useState(true);

  const [unassignId, setUnassignId] = useState(null);
  const [extendState, setExtendState] = useState({ id: null, date: null });
  const [nudgedIds, setNudgedIds] = useState(new Set());

  const [evaluations, setEvaluations] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);

  const loadTraining = useCallback(async () => {
    setTrainingLoading(true);
    try {
      const res = await getTraining(trainingId);
      setTraining(res.data?.data);
    } finally {
      setTrainingLoading(false);
    }
  }, [trainingId]);

  const fetchAssignments = useCallback((params) => {
    setFetching(true);
    listTrainingAssignments(trainingId, params)
      .then((res) => {
        const d = res.data?.data;
        if (d?.data) {
          setData(d.data);
          setPagination({
            totalRowCount: d.totalRowCount || 0,
            pageIndex: (d.pageIndex || 1) - 1,
            pageSize: d.pageSize || 10,
          });
        } else {
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
        }
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [trainingId]);

  const loadEvaluations = useCallback(async () => {
    setEvalLoading(true);
    try {
      const res = await getTrainingEvaluationResults(trainingId, { pageNumber: 1, pageSize: 50 });
      setEvaluations(res.data?.data || null);
    } finally {
      setEvalLoading(false);
    }
  }, [trainingId]);

  useEffect(() => { loadTraining(); }, [loadTraining]);
  useEffect(() => { fetchAssignments(queryParams); }, [fetchAssignments, queryParams]);

  const statusLabel = useMemo(() => ({
    assigned: t('training.assignmentStatus.assigned'),
    not_started: t('training.assignmentStatus.not_started'),
    in_progress: t('training.assignmentStatus.in_progress'),
    completed: t('training.assignmentStatus.completed'),
    failed: t('training.assignmentStatus.failed'),
    expired: t('training.assignmentStatus.expired'),
  }), [t]);

  const handleNudge = useCallback(async (assignmentId) => {
    if (nudgedIds.has(assignmentId)) return;
    try {
      await nudgeTrainingAssignment(assignmentId);
      setNudgedIds((prev) => new Set(prev).add(assignmentId));
      // 30-second debounce per assignment — prevents accidental spam.
      setTimeout(() => {
        setNudgedIds((prev) => {
          const next = new Set(prev);
          next.delete(assignmentId);
          return next;
        });
      }, 30000);
    } catch { /* interceptor */ }
  }, [nudgedIds]);

  const handleGrantBonus = useCallback(async (row) => {
    try {
      await grantBonusAttempts(row.id);
      fetchAssignments(queryParams);
    } catch { /* interceptor */ }
  }, [fetchAssignments, queryParams]);

  const handleAssignSave = useCallback(async () => {
    if (!startDate || !dueDate) return;
    setSaving(true);
    try {
      const startNormalized = new Date(startDate);
      startNormalized.setHours(0, 0, 0, 0);
      const dueNormalized = new Date(dueDate);
      dueNormalized.setHours(23, 59, 59, 0);
      await assignTraining(trainingId, {
        targets,
        startDate: startNormalized.toISOString(),
        dueDate: dueNormalized.toISOString(),
        isMandatory,
        notificationEnabled,
      });
      setTargets(emptyTargets());
      setDrawerOpen(false);
      fetchAssignments(queryParams);
    } catch { /* interceptor */ }
    setSaving(false);
  }, [trainingId, targets, startDate, dueDate, isMandatory, notificationEnabled, fetchAssignments, queryParams]);

  const handleUnassignConfirm = useCallback(async () => {
    if (!unassignId) return;
    await unassignTraining(unassignId);
    setUnassignId(null);
    fetchAssignments(queryParams);
  }, [unassignId, fetchAssignments, queryParams]);

  const handleExtendConfirm = useCallback(async () => {
    if (!extendState.id || !extendState.date) return;
    await extendTrainingAssignment(extendState.id, extendState.date.toISOString());
    setExtendState({ id: null, date: null });
    fetchAssignments(queryParams);
  }, [extendState, fetchAssignments, queryParams]);

  const baseColumns = useMemo(() => [
    {
      name: 'userFullName',
      label: t('training.learner'),
      options: {
        filter: true, sort: true, filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={value || '-'} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    {
      name: 'status',
      label: t('training.status'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['assigned', 'not_started', 'in_progress', 'completed', 'failed', 'expired'],
          renderValue: (v) => statusLabel[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabel[v] || v },
        customBodyRender: (value) => (
          <StatusBadge $s={value}>{statusLabel[value] || value}</StatusBadge>
        ),
      },
    },
    {
      name: 'quizPassed',
      label: t('training.quizResult'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('training.passed') : t('training.quizFailed'),
        },
        customFilterListOptions: {
          render: (v) => v === 'true' ? t('training.passed') : t('training.quizFailed'),
        },
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (row?.quizPassed === true) return <StatusBadge $s="completed">{t('training.passed')}</StatusBadge>;
          if (row?.quizPassed === false && row?.quizAttemptCount > 0) return <StatusBadge $s="failed">{t('training.quizFailed')}</StatusBadge>;
          return <span style={{ color: '#A0A8B4', fontSize: 12 }}>—</span>;
        },
      },
    },
    {
      name: 'quizBestScore',
      label: t('training.score'),
      options: {
        filter: true, sort: true, filterType: 'custom',
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row?.quizBestScore != null ? `${row.quizBestScore}/${row.quizMaxScore}` : '—';
        },
        customFilterListOptions: {
          render: (v) => {
            if (!v || !v[0]) return [];
            const f = v[0];
            const lbl = t('training.score');
            if (f.op === 'between') return [`${lbl}: ${f.val[0]} — ${f.val[1]}`];
            if (f.op === 'gte') return [`${lbl} >= ${f.val}`];
            if (f.op === 'lte') return [`${lbl} <= ${f.val}`];
            return [];
          },
          update: (filterList, filterPos, index) => {
            filterList[index] = [];
            return filterList;
          },
        },
        filterOptions: {
          logic: () => false,
          display: (fl, onChange, i, col) => {
            const val = fl[i]?.[0];
            const minVal = val?.op === 'between' ? (val.val?.[0] ?? '') : (val?.op === 'gte' ? (val.val ?? '') : '');
            const maxVal = val?.op === 'between' ? (val.val?.[1] ?? '') : (val?.op === 'lte' ? (val.val ?? '') : '');
            const apply = (sRaw, eRaw) => {
              const s = sRaw === '' || sRaw == null ? '' : Number(sRaw);
              const e = eRaw === '' || eRaw == null ? '' : Number(eRaw);
              if (s !== '' && e !== '') fl[i] = [{ op: 'between', val: [s, e] }];
              else if (s !== '') fl[i] = [{ op: 'gte', val: s }];
              else if (e !== '') fl[i] = [{ op: 'lte', val: e }];
              else fl[i] = [];
              onChange(fl[i], i, col);
            };
            return (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <TextField
                  type="number"
                  variant="standard"
                  size="small"
                  fullWidth
                  label={`${t('training.score')} (min)`}
                  value={minVal}
                  onChange={(e) => apply(e.target.value, maxVal)}
                />
                <span style={{ color: '#A0A8B4' }}>—</span>
                <TextField
                  type="number"
                  variant="standard"
                  size="small"
                  fullWidth
                  label={`${t('training.score')} (max)`}
                  value={maxVal}
                  onChange={(e) => apply(minVal, e.target.value)}
                />
              </div>
            );
          },
        },
      },
    },
    {
      name: 'attempts',
      label: t('training.attempts'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row?.quizMaxAttempts != null
            ? `${row.quizAttemptCount || 0}/${row.quizMaxAttempts + (row.bonusAttempts || 0)}`
            : '—';
        },
      },
    },
    dateFilterColumn('dueDate', t('training.dueDate'), i18n.language),
  ], [t, i18n.language, data, statusLabel]);

  const actionOptions = useMemo(() => [
    {
      type: 'extend',
      label: t('training.extendDueDate'),
      icon: Calendar,
      color: '#64748b',
      onClick: (row) => setExtendState({ id: row.id, date: new Date(row.dueDate) }),
    },
    {
      type: 'bonus',
      label: t('training.grantBonus'),
      icon: RotateCcw,
      color: '#0291EF',
      onClick: handleGrantBonus,
      condition: (row) => row.status === 'failed',
    },
    {
      type: 'nudge',
      label: t('training.nudge'),
      icon: Bell,
      color: '#F59E0B',
      onClick: (row) => handleNudge(row.id),
      condition: (row) =>
        (row.status === 'assigned' || row.status === 'not_started' || row.status === 'in_progress')
        && !nudgedIds.has(row.id),
    },
    {
      type: 'delete',
      label: t('training.unassign'),
      onClick: (row) => setUnassignId(row.id),
    },
  ], [t, handleGrantBonus, handleNudge, nudgedIds]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleAddOpen = () => {
    setTargets(emptyTargets());
    setStartDate(new Date());
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setDueDate(d);
    setIsMandatory(true);
    setNotificationEnabled(true);
    setDrawerOpen(true);
  };

  if (trainingLoading) return <PageContainer>{t('common.loading')}</PageContainer>;
  if (!training) return null;

  const canAssign = training.status === 'published';
  const targetsEmpty = targets.isEmpty ?? false;

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('training.pageTitle'), path: '/trainings' },
          { label: training.title, path: `/trainings/${training.id}/edit` },
          { label: t('training.assignments') },
        ]} />
        <Title>{t('training.assignments')}</Title>
      </StickyHeader>

      <Content>
        {!canAssign && (
          <Warning>{t('training.onlyPublishedAssignable')}</Warning>
        )}

        <DataTable
        title={t('training.currentAssignments')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canAssign}
        newButtonLabel={t('training.newAssignment')}
        onNewButtonClick={handleAddOpen}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />

      <Card>
        <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('training.evaluationResultsTab')}</span>
          <Button variant="outline" onClick={loadEvaluations} disabled={evalLoading}>
            {evalLoading ? '...' : t('common.refresh') || 'Yenile'}
          </Button>
        </SectionTitle>

        {evaluations == null ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>
            {t('training.loadEvaluationsPrompt') || 'Değerlendirme sonuçlarını görüntülemek için "Yenile" butonuna tıklayın.'}
          </div>
        ) : evaluations.totalCount === 0 ? (
          <div style={{ color: '#A0A8B4', fontSize: 13 }}>{t('training.evaluationResultsEmpty')}</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{t('training.evaluationAverageRating')}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#004242' }}>
                  {evaluations.averageRating ? evaluations.averageRating.toFixed(2) : '—'} / 5
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{t('training.evaluationTotal')}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#004242' }}>{evaluations.totalCount}</div>
              </div>
              <div style={{ flex: 1 }}>
                {(evaluations.distribution || []).map((b) => (
                  <div key={b.rating} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 12, width: 18, color: '#475569' }}>{b.rating}★</span>
                    <div style={{ flex: 1, height: 8, background: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${evaluations.totalCount > 0 ? (b.count / evaluations.totalCount * 100) : 0}%`,
                        height: '100%',
                        background: '#0291EF',
                      }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#64748b', width: 30 }}>{b.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {(evaluations.entries?.data || []).map((e) => (
              <div key={e.id} style={{
                borderTop: '1px solid #E2E8F0',
                padding: '12px 0',
                fontSize: 13,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <b style={{ color: '#004242' }}>{e.userFullName}</b>
                  <span style={{ color: '#64748b' }}>
                    {'★'.repeat(e.rating)}{'☆'.repeat(5 - e.rating)}
                  </span>
                </div>
                {e.contentComment && <div style={{ color: '#475569', marginBottom: 4 }}>{e.contentComment}</div>}
                {e.instructorComment && <div style={{ color: '#475569', fontStyle: 'italic' }}>{e.instructorComment}</div>}
              </div>
            ))}
          </>
        )}
      </Card>
      </Content>

      <ConfirmDialog
        isOpen={!!unassignId}
        title={t('training.unassign')}
        message={t('training.unassignConfirm')}
        onConfirm={handleUnassignConfirm}
        onCancel={() => setUnassignId(null)}
      />

      {extendState.id && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,42,42,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setExtendState({ id: null, date: null })}>
          <div style={{ background: '#fff', borderRadius: 6, padding: 24, width: 400 }} onClick={(e) => e.stopPropagation()}>
            <SectionTitle>{t('training.extendDueDate')}</SectionTitle>
            <DateTimePicker
              label={t('training.newDueDate')}
              value={extendState.date}
              onChange={(d) => setExtendState({ ...extendState, date: d })}
              mode="date"
            />
            <FooterRow>
              <Button variant="outline" onClick={() => setExtendState({ id: null, date: null })}>{t('common.cancel')}</Button>
              <Button onClick={handleExtendConfirm} disabled={!extendState.date}>{t('common.save')}</Button>
            </FooterRow>
          </div>
        </div>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 380,
          margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000,
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('training.newAssignment')}</DrawerTitle>
          <CloseBtn onClick={() => setDrawerOpen(false)}><X size={20} /></CloseBtn>
        </DrawerHeader>
        <DrawerBody>
          <AssignmentTargetPicker value={targets} onChange={setTargets} />
          <FormRow>
            <DateTimePicker label={t('training.startDate')} value={startDate} onChange={setStartDate} mode="date" required />
            <DateTimePicker label={t('training.dueDate')} value={dueDate} onChange={setDueDate} mode="date" required />
          </FormRow>
          <FormRow>
            <CheckboxInput label={t('training.isMandatory')} checked={isMandatory} onChange={setIsMandatory} />
            <CheckboxInput label={t('training.notifyLearners')} checked={notificationEnabled} onChange={setNotificationEnabled} />
          </FormRow>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={() => setDrawerOpen(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={handleAssignSave} disabled={saving || !canAssign || targetsEmpty}>
            {saving ? '...' : t('training.assign')}
          </Button>
        </DrawerFooter>
      </Drawer>
    </PageContainer>
  );
}
